import { Resources } from 'constants/resources';

import React, { useEffect } from 'react';

import { useNormalizeReferences } from 'hooks/useNormalizeReferences';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RaRecord, required, useGetOne, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Row } from 'shared/mui/Row/Row';
import { AutocompleteInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { fieldWidth } from 'themes/layout';
import { Gateway } from 'types/embedded/gateway';
import { cleanEmpty } from 'utils/cleanEmpty';

import { MerchantsShowLimitsEditDialogFormSkeleton } from './MerchantsShowLimitsEditDialogFormSkeleton';

type Props = {
  merchantId: string | undefined;
  errors: string[];
};

export const MerchantsShowLimitsEditDialogForm: React.FC<Props> = ({ merchantId, errors }) => {
  const translate = useTypedTranslate();

  const { watch, setError } = useFormContext();
  const record = useRecordContext();
  const gatewayWatched = watch('gateway');

  const { data: gateway, isLoading: isGatewayLoading } = useGetOne<Gateway>(
    Resources.Gateways,
    { id: gatewayWatched },
    { enabled: !!gatewayWatched && typeof gatewayWatched !== 'object' },
  );

  const { ready } = useNormalizeReferences({
    referenceFields: ['gateway', 'currency'],
    externalRecord: record,
  });

  useEffect(() => {
    errors?.forEach((error: string) => {
      const [field, message] = error.split(':');
      setError(field, { type: 'custom', message });
    });
  }, [errors]);

  if (!ready || isGatewayLoading) {
    return <MerchantsShowLimitsEditDialogFormSkeleton />;
  }

  return (
    <Box>
      <Row marginBottom={3}>
        <ReferenceInput
          filter={cleanEmpty({
            merchantAccounts: {
              merchants: [merchantId],
            },
          })}
          perPage={200}
          reference="gateways"
          source="gateway"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.gateway')}
            optionText="name"
            readOnly={true}
            source="gateway"
            sx={fieldWidth.default}
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceInput
          filter={{
            id: [
              ...(gateway?.supportedCurrencies?.map((v) => v?.['@id']) || []),
              gateway?.defaultCurrency?.['@id'],
            ],
            active: true,
          }}
          perPage={1000}
          reference="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteInput
            disabled={!gatewayWatched}
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            helperText={false}
            label={translate('app.filter.currency')}
            optionText={(record: RaRecord) => `${record?.alphaCode}, ${record?.name}`}
            source="currency"
            sx={fieldWidth.default}
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      </Row>
      <Row marginBottom={3}>
        <TextInput
          helperText={false}
          label={translate('app.limits.minDepositAmount')}
          source="paymentMin"
          sx={fieldWidth.default}
          variant="outlined"
        />
        <TextInput
          helperText={false}
          label={translate('app.limits.maxDepositAmount')}
          source="paymentMax"
          sx={fieldWidth.default}
          variant="outlined"
        />
      </Row>
      <Row>
        <TextInput
          helperText={false}
          label={translate('app.limits.minWithdrawalAmount')}
          source="payoutMin"
          sx={fieldWidth.default}
          variant="outlined"
        />
        <TextInput
          helperText={false}
          label={translate('app.limits.maxWithdrawalAmount')}
          source="payoutMax"
          sx={fieldWidth.default}
          variant="outlined"
        />
      </Row>
    </Box>
  );
};
